import React, { useEffect, useRef, useState } from 'react';
import {
  SYSTEM_NOTIFICATION_MAX_LEN,
  notifySegment,
  previewSegment,
} from '../api/adminApi';
import {
  SegmentDescription,
  SegmentPreview,
  SystemNotificationOk,
  SystemNotificationResult,
} from '../types';
import ModalShell from './ModalShell';

interface StartNotificationModalProps {
  segment: SegmentDescription;
  onClose: () => void;
  onSent: (result: SystemNotificationOk) => void;
  /**
   * Override the default segment-keyed notification call. The
   * Overview "Active now" launcher passes a closure that hits the
   * live-cohort endpoint instead, so the same modal can drive
   * either a segment notification or an ad-hoc live blast without
   * duplicating the form layout.
   */
  notify?: (message: string) => Promise<SystemNotificationResult>;
  /** If supplied, the modal renders this preview as-is and skips the default fetch. */
  initialPreview?: SegmentPreview | null;
  /** Skip the default segment preview fetch entirely (e.g. for ad-hoc audiences). */
  skipDefaultPreview?: boolean;
}

/**
 * Bulk system-notification composer for a segment. Intentionally
 * mirrors {@link StartCampaignModal} structurally — same layout,
 * same recipient-preview block, same confirm-before-send pattern —
 * so the admin's mental model is "pick a segment, pick a channel
 * (email or notification), send it" without two different UX
 * vocabularies.
 *
 * <p>Differs from email campaigns in three ways:
 *
 * <ol>
 *   <li>No CTA, preheader, or sender override — system notifications
 *       are short text-only payloads delivered to the in-app bell,
 *       not the inbox.</li>
 *   <li>No warm-up / daily-cap pacing. Calls
 *       {@link notifySegment} once and gets a synchronous
 *       fan-out result. The success toast surfaces the actual
 *       sent / missing counts.</li>
 *   <li>280-character ceiling enforced both client-side and on the
 *       backend ({@code SystemNotificationService.MAX_MESSAGE_LENGTH}).</li>
 * </ol>
 */
const StartNotificationModal: React.FC<StartNotificationModalProps> = ({
  segment,
  onClose,
  onSent,
  notify,
  initialPreview,
  skipDefaultPreview,
}) => {
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<SegmentPreview | null>(initialPreview ?? null);
  const [showEmails, setShowEmails] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (skipDefaultPreview) return;
    let mounted = true;
    (async () => {
      const p = await previewSegment(segment.key, 20);
      if (mounted) setPreview(p);
    })();
    return () => {
      mounted = false;
    };
  }, [segment.key, skipDefaultPreview]);

  useEffect(() => {
    window.setTimeout(() => taRef.current?.focus(), 0);
  }, []);

  const trimmed = message.trim();
  const remaining = SYSTEM_NOTIFICATION_MAX_LEN - message.length;
  const empty = trimmed.length === 0;
  const overLimit = remaining < 0;
  const recipientCount = preview?.totalRecipients ?? segment.recipientCount;
  const canSubmit = !empty && !overLimit && !busy && recipientCount > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const confirmed = window.confirm(
      `Send this notification to ~${recipientCount.toLocaleString()} users in "${segment.label}"?\n\n` +
        `${trimmed}\n\n` +
        `Delivery is instant and in-app only — no email, no rate limiting.`,
    );
    if (!confirmed) return;
    try {
      setBusy(true);
      setError(null);
      const result = notify
        ? await notify(trimmed)
        : await notifySegment(segment.key, trimmed);
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      onSent(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      open
      onClose={onClose}
      busy={busy}
      size="md"
      eyebrow="System notification"
      title="Send in-app message"
      description={
        <>
          Audience:{' '}
          <span className="font-medium text-gray-700">{segment.label}</span> ·{' '}
          {recipientCount.toLocaleString()} recipients
        </>
      }
      headerEnd={
        <div className="text-[11px] text-[#0b3a55] bg-[#eef7fb] border border-[#94c2dc]/40 rounded-md px-2 py-1 leading-tight max-w-[260px]">
          In-app bell only. Delivers instantly &mdash; no warm-up or daily cap.
        </div>
      }
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 sm:py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
            disabled={!canSubmit}
            className="px-4 py-2.5 sm:py-2 bg-gradient-to-b from-[#01183a] to-[#09264f] text-white text-sm font-medium rounded-lg shadow-sm hover:from-[#09264f] hover:to-[#0b3a55] disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-600 disabled:cursor-not-allowed disabled:shadow-none"
            title={
              empty
                ? 'Type a message first.'
                : overLimit
                  ? 'Message exceeds 280 characters.'
                  : `Send to ~${recipientCount.toLocaleString()} users.`
            }
          >
            {busy ? 'Sending…' : `Send to ${recipientCount.toLocaleString()}`}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Message<span className="text-red-500 ml-0.5">*</span>
            </label>
            <span className="text-[10px] text-gray-400">Plain text · no links rendered</span>
          </div>
          <textarea
            ref={taRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={SYSTEM_NOTIFICATION_MAX_LEN + 50}
            className={`w-full text-base sm:text-sm px-3.5 py-3 text-gray-900 placeholder-gray-400 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow leading-relaxed ${
              overLimit ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Quick heads-up — keep it short and concrete."
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (canSubmit) handleSubmit(e as unknown as React.FormEvent);
              }
            }}
          />
          <div className="flex items-center justify-between mt-1.5 text-[11px]">
            <span className={overLimit ? 'text-red-600 font-medium' : 'text-gray-400'}>
              {remaining} {remaining === 1 ? 'character' : 'characters'} left
            </span>
            <span className="text-gray-400">⌘ ⏎ to send</span>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3.5 text-xs">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-gray-700 font-medium">
              {preview
                ? `${preview.totalRecipients.toLocaleString()} recipients in this segment`
                : 'Loading preview…'}
            </span>
            {preview && preview.emails.length > 0 && (
              <button
                type="button"
                onClick={() => setShowEmails((v) => !v)}
                className="text-violet-600 hover:underline"
              >
                {showEmails ? 'Hide sample' : `Show first ${preview.emails.length}`}
              </button>
            )}
          </div>
          {showEmails && preview && (
            <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto font-mono text-[11px] text-gray-600">
              {preview.emails.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          {preview && preview.totalRecipients === 0 && (
            <div className="mt-2 text-amber-700">
              This segment has no in-app users (cold-leads or fully suppressed lists resolve
              to zero recipients for notifications).
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
            {error}
          </div>
        )}
      </form>
    </ModalShell>
  );
};

export default StartNotificationModal;
